import { cn, focusHalo } from "@pax/ui";
import { ZoomIn, ZoomOut } from "lucide-react";
import { type KeyboardEvent, type PointerEvent, useRef } from "react";
import {
  type Crop,
  clampedCrop,
  cropPlacement,
  draggedCrop,
  type LocalImage,
  MAX_CROP_ZOOM,
} from "../image-crop";

const KEY_STEP = 0.02;
const KEY_ZOOM = 1.1;
const WHEEL_ZOOM_RATE = 500;

/** A picked image shown as its crop. The parent is the frame: `relative`, `overflow-hidden`, and the frame's shape. */
export function CroppedImage({
  image,
  crop,
  frameAspect,
}: {
  image: LocalImage;
  crop: Crop;
  frameAspect: number;
}) {
  return (
    <img
      src={image.url}
      alt=""
      draggable={false}
      className="pointer-events-none absolute max-w-none select-none"
      style={cropPlacement(crop, image.aspect, frameAspect)}
    />
  );
}

/**
 * Where the owner picks the part of an image to keep: drag the image in the frame, and zoom with
 * the slider, a trackpad, or the scroll wheel. The frame shows exactly what the processor keeps.
 */
export function ImageCropper({
  image,
  frameAspect,
  round,
  crop,
  onCropChange,
}: {
  image: LocalImage;
  frameAspect: number;
  /** Shows the circle the avatar is drawn in. The file keeps the whole square. */
  round: boolean;
  crop: Crop;
  onCropChange: (crop: Crop) => void;
}) {
  const frame = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ pointerId: number; x: number; y: number; crop: Crop } | null>(null);

  const zoomTo = (zoom: number) =>
    onCropChange(clampedCrop({ ...crop, zoom }, image.aspect, frameAspect));

  function handlePointerDown(event: PointerEvent<HTMLButtonElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, crop };
  }

  function handlePointerMove(event: PointerEvent<HTMLButtonElement>) {
    const start = drag.current;
    const bounds = frame.current?.getBoundingClientRect();
    if (!start || start.pointerId !== event.pointerId || !bounds) return;
    onCropChange(
      draggedCrop(
        start.crop,
        image.aspect,
        frameAspect,
        (event.clientX - start.x) / bounds.width,
        (event.clientY - start.y) / bounds.height,
      ),
    );
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [KEY_STEP, 0],
      ArrowRight: [-KEY_STEP, 0],
      ArrowUp: [0, KEY_STEP],
      ArrowDown: [0, -KEY_STEP],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      onCropChange(draggedCrop(crop, image.aspect, frameAspect, move[0], move[1]));
    } else if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoomTo(crop.zoom * KEY_ZOOM);
    } else if (event.key === "-") {
      event.preventDefault();
      zoomTo(crop.zoom / KEY_ZOOM);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <button
        ref={frame}
        type="button"
        aria-roledescription="crop area"
        aria-label="Drag the image, or use the arrow keys, to move it. Plus and minus zoom."
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onWheel={event => zoomTo(crop.zoom * Math.exp(-event.deltaY / WHEEL_ZOOM_RATE))}
        onKeyDown={handleKeyDown}
        className={cn(
          "relative mx-auto block w-full cursor-grab touch-none overflow-hidden rounded-xl bg-foreground/5 select-none active:cursor-grabbing",
          round && "max-w-72",
          focusHalo,
        )}
        style={{ aspectRatio: frameAspect }}
      >
        <CroppedImage image={image} crop={crop} frameAspect={frameAspect} />
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 ring-1 ring-white/70 ring-inset",
            round ? "rounded-full shadow-[0_0_0_100vmax_rgb(0_0_0/0.45)]" : "rounded-[inherit]",
          )}
        />
      </button>

      <div className="mx-auto flex w-full max-w-72 items-center gap-2.5 text-muted-foreground">
        <ZoomOut aria-hidden className="size-4 shrink-0" />
        <input
          type="range"
          min={1}
          max={MAX_CROP_ZOOM}
          step={0.01}
          value={crop.zoom}
          aria-label="Zoom"
          onChange={event => zoomTo(Number(event.target.value))}
          className="h-1 min-w-0 flex-1 cursor-pointer accent-foreground"
        />
        <ZoomIn aria-hidden className="size-4 shrink-0" />
      </div>
    </div>
  );
}
